/**
 * Measure tab — right panel and form for the visual editor's measure mode.
 * Phase 3: summary stats, tolerance bars, re-measure, quick-add table.
 *
 * @see redesign-phases.md — Phase 3
 */
import { useState } from "react";
import { ArrowLeft, Check, RefreshCw, X } from "lucide-react";
import { Button, Input, StatusPill } from "@flint/ui";
import type { measurements as measurementsContract } from "@flint/api-contract";
import {
  computeMeasurementStats,
  type MeasurementStats,
} from "@flint/domain/garment/measurements/stats";
import { KV, Tile, ToleranceBar } from "./canvas-helpers.tsx";

/* ── Types ────────────────────────────────────────────────────────────── */

export type Measurement =
  measurementsContract.MeasurementsViewWire["measurements"][number];

/* ── Utilities ────────────────────────────────────────────────────────── */

/** mm → display cm string with Indian locale formatting. */
export const cm = (mm: number) =>
  (mm / 10).toLocaleString("en-IN", { maximumFractionDigits: 1 });

/** Display cm string → mm integer; null when blank or invalid. */
export const cmToMm = (s: string): number | null => {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) : null;
};

/* ── Summary utilities ────────────────────────────────────────────────── */

// The per-measurement pass/fail + summary aggregation lives in the domain
// (`@flint/domain/garment/measurements/stats`) so the tech-pack PDF and QC
// summarise measurements identically. `bannerTone` below is UI presentation.

export function bannerTone(passRate: number): "ok" | "warn" | "bad" {
  if (passRate >= 100) return "ok";
  if (passRate >= 70) return "warn";
  return "bad";
}

const BANNER_COLORS = {
  ok: {
    border: "border-emerald-200",
    bg: "bg-emerald-50",
    fill: "bg-emerald-500",
    text: "text-emerald-700",
  },
  warn: {
    border: "border-amber-200",
    bg: "bg-amber-50",
    fill: "bg-amber-500",
    text: "text-amber-700",
  },
  bad: {
    border: "border-red-200",
    bg: "bg-red-50",
    fill: "bg-bad",
    text: "text-bad",
  },
} as const;

/* ── Auto-pin placement for quick-add ─────────────────────────────────── */

export function autoPlacePins(
  count: number,
): Array<{ pinX: number; pinY: number }> {
  const startY = 25;
  const endY = 85;
  if (count <= 0) return [];
  if (count === 1) return [{ pinX: 50, pinY: 55 }];
  const step = (endY - startY) / (count - 1);
  return Array.from({ length: count }, (_, i) => ({
    pinX: 50,
    pinY: startY + step * i,
  }));
}

/* ── PassFailBanner ───────────────────────────────────────────────────── */

function PassFailBanner({ stats }: { stats: MeasurementStats }) {
  const tone = bannerTone(stats.passRate);
  const colors = BANNER_COLORS[tone];

  return (
    <div
      className={`rounded-md border-l-4 ${colors.border} ${colors.bg} px-3 py-2`}
    >
      <div className="flex items-center justify-between text-xs font-semibold">
        <span className={colors.text}>
          {stats.passing}/{stats.total} in tolerance
        </span>
        <span className={colors.text}>{stats.passRate}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/60">
        <div
          className={`h-full rounded-full transition-all ${colors.fill}`}
          style={{ width: `${stats.passRate}%` }}
        />
      </div>
    </div>
  );
}

/* ── SummaryTiles ─────────────────────────────────────────────────────── */

function SummaryTiles({ stats }: { stats: MeasurementStats }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <Tile
        label="POMs"
        value={String(stats.total)}
        sub={`${stats.total} measured`}
      />
      <Tile
        label="Pass rate"
        value={`${stats.passRate}%`}
        sub={`${stats.passing} passing`}
      />
      <Tile
        label="Worst dev"
        value={stats.worstPom ? `${cm(stats.worstDevMm)} cm` : "—"}
        sub={stats.worstPom ?? "none"}
      />
    </div>
  );
}

/* ── ModeToggle ───────────────────────────────────────────────────────── */

type AddMode = "click" | "quick-add";

function ModeToggle({
  mode,
  onChange,
}: {
  mode: AddMode;
  onChange: (m: AddMode) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-sm border border-line">
      {(["click", "quick-add"] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`px-3 py-1 text-xs font-semibold transition ${
            mode === m
              ? "bg-sky-50 text-sky-700"
              : "bg-paper text-muted hover:bg-bg"
          }`}
        >
          {m === "click" ? "Click to place" : "Quick-add"}
        </button>
      ))}
    </div>
  );
}

/* ── QuickAddTable ────────────────────────────────────────────────────── */

type QuickAddRow = {
  pom: string;
  spec: string;
  tol: string;
  actual: string;
};

const EMPTY_ROW: QuickAddRow = { pom: "", spec: "", tol: "0.5", actual: "" };

function QuickAddTable({
  quickAddPending,
  onSaveAll,
}: {
  quickAddPending: boolean;
  onSaveAll: (
    rows: Array<{
      pomCode: string;
      specMm: number;
      tolMm: number;
      actualMm: number;
      pinX: number;
      pinY: number;
    }>,
  ) => void;
}) {
  const [rows, setRows] = useState<QuickAddRow[]>([
    { ...EMPTY_ROW },
    { ...EMPTY_ROW },
    { ...EMPTY_ROW },
  ]);

  const updateRow = (idx: number, patch: Partial<QuickAddRow>) => {
    setRows((cur) =>
      cur.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const removeRow = (idx: number) => {
    setRows((cur) => cur.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setRows((cur) => [...cur, { ...EMPTY_ROW }]);
  };

  const validRows = rows
    .map((r) => {
      const specMm = cmToMm(r.spec);
      const tolMm = cmToMm(r.tol);
      const actualMm = cmToMm(r.actual);
      if (r.pom.trim() === "" || specMm === null || tolMm === null || actualMm === null) {
        return null;
      }
      return { pomCode: r.pom.trim(), specMm, tolMm, actualMm };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  const handleSave = () => {
    if (validRows.length === 0) return;
    const pins = autoPlacePins(validRows.length);
    const payload = validRows.map((r, i) => ({
      ...r,
      pinX: pins[i]!.pinX,
      pinY: pins[i]!.pinY,
    }));
    onSaveAll(payload);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">
        Spec sheet — values in cm
      </div>
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-end gap-1.5">
          <label className="flex min-w-0 flex-1 flex-col gap-0.5 text-xs text-muted">
            {idx === 0 ? "POM" : null}
            <Input
              value={row.pom}
              onChange={(e) => updateRow(idx, { pom: e.currentTarget.value })}
              placeholder="Chest"
              className="h-7 text-xs"
            />
          </label>
          <label className="flex w-14 flex-col gap-0.5 text-xs text-muted">
            {idx === 0 ? "Spec" : null}
            <Input
              value={row.spec}
              onChange={(e) => updateRow(idx, { spec: e.currentTarget.value })}
              inputMode="decimal"
              placeholder="102"
              className="h-7 text-xs"
            />
          </label>
          <label className="flex w-12 flex-col gap-0.5 text-xs text-muted">
            {idx === 0 ? "Tol ±" : null}
            <Input
              value={row.tol}
              onChange={(e) => updateRow(idx, { tol: e.currentTarget.value })}
              inputMode="decimal"
              placeholder="0.5"
              className="h-7 text-xs"
            />
          </label>
          <label className="flex w-14 flex-col gap-0.5 text-xs text-muted">
            {idx === 0 ? "Actual" : null}
            <Input
              value={row.actual}
              onChange={(e) => updateRow(idx, { actual: e.currentTarget.value })}
              inputMode="decimal"
              placeholder="102.5"
              className="h-7 text-xs"
            />
          </label>
          <button
            type="button"
            onClick={() => removeRow(idx)}
            className="mb-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-sm text-muted transition hover:bg-bg hover:text-bad"
            title="Remove row"
          >
            <X size={12} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          intent="ghost"
          className="border border-line"
          onClick={addRow}
        >
          + Add row
        </Button>
        <Button
          size="sm"
          disabled={quickAddPending || validRows.length === 0}
          onClick={handleSave}
        >
          {quickAddPending
            ? "Saving…"
            : `Save all (${validRows.length})`}
        </Button>
      </div>
    </div>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────── */

export function MeasurePanel({
  measurements,
  selected,
  addingAt,
  view,
  pending,
  prefill,
  quickAddPending,
  onPick,
  onBack,
  onCancelAdd,
  onSave,
  onRemeasure,
  onQuickAddSaveAll,
}: {
  measurements: Measurement[];
  selected: Measurement | null;
  addingAt: { x: number; y: number } | null;
  view: "front" | "back";
  pending: boolean;
  prefill?: { pomCode: string; specMm: number; tolMm: number } | null;
  quickAddPending: boolean;
  onPick: (id: string) => void;
  onBack: () => void;
  onCancelAdd: () => void;
  onSave: (f: {
    pomCode: string;
    specMm: number;
    tolMm: number;
    actualMm: number;
  }) => void;
  onRemeasure: (m: Measurement) => void;
  onQuickAddSaveAll: (
    rows: Array<{
      pomCode: string;
      specMm: number;
      tolMm: number;
      actualMm: number;
      pinX: number;
      pinY: number;
    }>,
  ) => void;
}) {
  const [addMode, setAddMode] = useState<AddMode>("click");

  /* ── Adding / re-measure form ───────────────────────────────────────── */
  if (addingAt) {
    return (
      <MeasureForm
        key={`${addingAt.x},${addingAt.y}`}
        view={view}
        pending={pending}
        prefill={prefill}
        onCancel={onCancelAdd}
        onSave={onSave}
      />
    );
  }

  /* ── Selected detail view ───────────────────────────────────────────── */
  if (selected) {
    const dev = selected.deviationMm;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`grid h-7 w-7 place-items-center rounded-full text-paper ${selected.inTolerance ? "bg-emerald-500" : "bg-bad"}`}
          >
            {selected.inTolerance ? <Check size={14} /> : <X size={14} />}
          </span>
          <h3 className="flex-1 text-sm font-semibold text-ink">
            {selected.pomCode}
          </h3>
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to measurements list"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        </div>
        <dl className="flex flex-col gap-1.5 text-sm">
          <KV k="Spec" v={`${cm(selected.specMm)} ${selected.uom}`} />
          <KV
            k="Tolerance"
            v={`± ${cm(selected.tolMm)} ${selected.uom}`}
          />
          <KV k="Actual" v={`${cm(selected.actualMm)} ${selected.uom}`} />
          <KV
            k="Deviation"
            v={
              <span
                className={
                  selected.inTolerance ? "text-emerald-600" : "text-bad"
                }
              >
                {dev > 0 ? "+" : ""}
                {cm(dev)} {selected.uom}
              </span>
            }
          />
          <KV
            k="Result"
            v={
              <StatusPill
                tone={selected.inTolerance ? "ok" : "bad"}
                size="sm"
                dot
              >
                {selected.inTolerance ? "In tolerance" : "Out of tolerance"}
              </StatusPill>
            }
          />
          <KV
            k="Face"
            v={<span className="capitalize">{selected.pinView}</span>}
          />
        </dl>

        <ToleranceBar
          specMm={selected.specMm}
          tolMm={selected.tolMm}
          actualMm={selected.actualMm}
          inTolerance={selected.inTolerance}
        />

        <p className="text-xs text-muted">
          Re-measure by adding a new reading for the same POM — the latest one
          wins (append-only).
        </p>
        <Button
          size="sm"
          intent="ghost"
          className="border border-line"
          onClick={() => onRemeasure(selected)}
        >
          <RefreshCw size={14} /> Re-measure
        </Button>
      </div>
    );
  }

  /* ── List view (default) ────────────────────────────────────────────── */
  const stats = computeMeasurementStats(measurements);
  const hasData = measurements.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">Measurements</h3>

      {hasData ? (
        <>
          <PassFailBanner stats={stats} />
          <SummaryTiles stats={stats} />
          <ModeToggle mode={addMode} onChange={setAddMode} />
        </>
      ) : null}

      {addMode === "quick-add" && hasData ? (
        <QuickAddTable
          quickAddPending={quickAddPending}
          onSaveAll={onQuickAddSaveAll}
        />
      ) : !hasData ? (
        <>
          <p className="rounded-sm border border-dashed border-line bg-bg px-2.5 py-2 text-xs text-muted">
            Click anywhere on the garment to drop a point-of-measure pin (spec ·
            tolerance · actual).
          </p>
          <ModeToggle mode={addMode} onChange={setAddMode} />
          {addMode === "quick-add" ? (
            <QuickAddTable
              quickAddPending={quickAddPending}
              onSaveAll={onQuickAddSaveAll}
            />
          ) : null}
        </>
      ) : (
        <ul className="flex flex-col gap-1">
          {measurements.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => onPick(m.id)}
                className="flex w-full flex-col gap-1 rounded-sm border border-line bg-paper px-2 py-1.5 text-left text-xs transition hover:bg-bg"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${m.inTolerance ? "bg-emerald-500" : "bg-bad"}`}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium text-ink">
                    {m.pomCode}
                  </span>
                  <span className="shrink-0 font-mono text-muted">
                    {cm(m.actualMm)}/{cm(m.specMm)}
                  </span>
                </div>
                <ToleranceBar
                  specMm={m.specMm}
                  tolMm={m.tolMm}
                  actualMm={m.actualMm}
                  inTolerance={m.inTolerance}
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Form ─────────────────────────────────────────────────────────────── */

function MeasureForm({
  view,
  pending,
  prefill,
  onCancel,
  onSave,
}: {
  view: "front" | "back";
  pending: boolean;
  prefill?: { pomCode: string; specMm: number; tolMm: number } | null | undefined;
  onCancel: () => void;
  onSave: (f: {
    pomCode: string;
    specMm: number;
    tolMm: number;
    actualMm: number;
  }) => void;
}) {
  const isRemeasure = prefill != null;

  const [pom, setPom] = useState(isRemeasure ? prefill.pomCode : "");
  const [spec, setSpec] = useState(
    isRemeasure ? String(prefill.specMm / 10) : "",
  );
  const [tol, setTol] = useState(
    isRemeasure ? String(prefill.tolMm / 10) : "0.5",
  );
  const [actual, setActual] = useState("");

  const specMm = cmToMm(spec);
  const tolMm = cmToMm(tol);
  const actualMm = cmToMm(actual);
  const valid =
    pom.trim() !== "" && specMm !== null && tolMm !== null && actualMm !== null;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">
        {isRemeasure ? `Re-measure: ${prefill.pomCode}` : "New point of measure"}
      </h3>
      <p className="text-xs text-muted">
        {isRemeasure ? (
          "Enter the new actual value — POM, spec, and tolerance are locked."
        ) : (
          <>
            Placed on the{" "}
            <span className="font-medium capitalize">{view}</span> at the pin.
            Values in cm.
          </>
        )}
      </p>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        POM
        <Input
          value={pom}
          onChange={(e) => setPom(e.currentTarget.value)}
          placeholder="Chest"
          className="h-8 text-sm"
          autoFocus={!isRemeasure}
          readOnly={isRemeasure}
        />
      </label>
      <div className="grid grid-cols-3 gap-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
          Spec
          <Input
            value={spec}
            onChange={(e) => setSpec(e.currentTarget.value)}
            inputMode="decimal"
            placeholder="102"
            className="h-8 text-sm"
            readOnly={isRemeasure}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
          Tol ±
          <Input
            value={tol}
            onChange={(e) => setTol(e.currentTarget.value)}
            inputMode="decimal"
            placeholder="0.5"
            className="h-8 text-sm"
            readOnly={isRemeasure}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
          Actual
          <Input
            value={actual}
            onChange={(e) => setActual(e.currentTarget.value)}
            inputMode="decimal"
            placeholder="102.5"
            className="h-8 text-sm"
            autoFocus={isRemeasure}
          />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={pending || !valid}
          onClick={() => {
            if (valid)
              onSave({
                pomCode: pom.trim(),
                specMm: specMm!,
                tolMm: tolMm!,
                actualMm: actualMm!,
              });
          }}
        >
          {pending ? "Saving…" : isRemeasure ? "Save re-measure" : "Save measurement"}
        </Button>
        <Button
          size="sm"
          intent="ghost"
          className="border border-line"
          disabled={pending}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
